// Cookie Banner RGPD - drbooking
// Obligatoire pour conformité européenne

import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Cookie, Shield } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const COOKIE_CONSENT_KEY = 'drbooking_cookie_consent';

const cookieTexts = {
  fr: { title: 'Nous utilisons des cookies', desc: 'Pour améliorer votre expérience et analyser le trafic.', more: 'En savoir plus', reject: 'Refuser', customize: 'Personnaliser', accept: 'Tout accepter' },
  en: { title: 'We use cookies', desc: 'To improve your experience and analyze traffic.', more: 'Learn more', reject: 'Reject', customize: 'Customize', accept: 'Accept all' },
  de: { title: 'Wir verwenden Cookies', desc: 'Um Ihre Erfahrung zu verbessern und den Verkehr zu analysieren.', more: 'Mehr erfahren', reject: 'Ablehnen', customize: 'Anpassen', accept: 'Alle akzeptieren' },
  es: { title: 'Usamos cookies', desc: 'Para mejorar su experiencia y analizar el tráfico.', more: 'Saber más', reject: 'Rechazar', customize: 'Personalizar', accept: 'Aceptar todo' },
  pt: { title: 'Usamos cookies', desc: 'Para melhorar a sua experiência e analisar o tráfego.', more: 'Saber mais', reject: 'Recusar', customize: 'Personalizar', accept: 'Aceitar tudo' },
  it: { title: 'Utilizziamo i cookie', desc: 'Per migliorare la tua esperienza e analizzare il traffico.', more: 'Scopri di più', reject: 'Rifiuta', customize: 'Personalizza', accept: 'Accetta tutto' },
  ar: { title: 'نستخدم ملفات تعريف الارتباط', desc: 'لتحسين تجربتك وتحليل حركة المرور.', more: 'اعرف المزيد', reject: 'رفض', customize: 'تخصيص', accept: 'قبول الكل' },
  lb: { title: 'Mir benotzen Cookies', desc: 'Fir Är Erfarung ze verbesseren an de Verkéier ze analyséieren.', more: 'Méi gewuer ginn', reject: 'Refuséieren', customize: 'Personaliséieren', accept: 'Alles akzeptéieren' },
  bg: { title: 'Използваме бисквитки', desc: 'За подобряване на вашето изживяване и анализ на трафика.', more: 'Научете повече', reject: 'Откажи', customize: 'Настрой', accept: 'Приеми всички' },
  nl: { title: 'Wij gebruiken cookies', desc: 'Om uw ervaring te verbeteren en verkeer te analyseren.', more: 'Meer info', reject: 'Weiger', customize: 'Aanpassen', accept: 'Alles accepteren' },
  tr: { title: 'Çerez kullanıyoruz', desc: 'Deneyiminizi geliştirmek ve trafiği analiz etmek için.', more: 'Daha fazla', reject: 'Reddet', customize: 'Özelleştir', accept: 'Tümünü kabul et' }
};

const CookieBanner = () => {
  const { language } = useLanguage();
  const ct = cookieTexts[language] || cookieTexts.fr;
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to not show immediately on page load
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setIsVisible(false);
  };

  const acceptNecessaryOnly = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setIsVisible(false);
  };

  const acceptSelected = () => {
    const analyticsCheckbox = document.getElementById('cookie-analytics');
    const marketingCheckbox = document.getElementById('cookie-marketing');
    
    const consent = {
      necessary: true,
      analytics: analyticsCheckbox?.checked || false,
      marketing: marketingCheckbox?.checked || false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-lg"
      data-testid="cookie-banner"
    >
      <div className="max-w-6xl mx-auto">
        {!showDetails ? (
          // Simple view
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Cookie className="h-8 w-8 text-blue-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white">
                  {ct.title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {ct.desc}
                  <button 
                    onClick={() => setShowDetails(true)}
                    className="text-blue-600 hover:underline ml-1"
                  >
                    {ct.more}
                  </button>
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={acceptNecessaryOnly}
                data-testid="cookie-reject-btn"
              >
                {ct.reject}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDetails(true)}
              >
                {ct.customize}
              </Button>
              <Button
                size="sm"
                onClick={acceptAll}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="cookie-accept-btn"
              >
                {ct.accept}
              </Button>
            </div>
          </div>
        ) : (
          // Detailed view
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-blue-600" />
                <h3 className="font-semibold text-lg text-slate-900 dark:text-white">
                  Paramètres des cookies
                </h3>
              </div>
              <button 
                onClick={() => setShowDetails(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Nous utilisons des cookies pour améliorer votre expérience sur notre site. 
              Vous pouvez choisir les types de cookies que vous acceptez.
            </p>
            
            <div className="space-y-3">
              {/* Necessary cookies - always enabled */}
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white">
                    Cookies nécessaires
                  </h4>
                  <p className="text-xs text-slate-500">
                    Essentiels au fonctionnement du site (session, sécurité)
                  </p>
                </div>
                <input 
                  type="checkbox" 
                  checked 
                  disabled 
                  className="h-5 w-5 text-blue-600 rounded"
                />
              </div>
              
              {/* Analytics cookies */}
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white">
                    Cookies analytiques
                  </h4>
                  <p className="text-xs text-slate-500">
                    Nous aident à comprendre comment vous utilisez le site
                  </p>
                </div>
                <input 
                  type="checkbox" 
                  id="cookie-analytics"
                  defaultChecked
                  className="h-5 w-5 text-blue-600 rounded cursor-pointer"
                />
              </div>
              
              {/* Marketing cookies */}
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white">
                    Cookies marketing
                  </h4>
                  <p className="text-xs text-slate-500">
                    Permettent de vous proposer des contenus personnalisés
                  </p>
                </div>
                <input 
                  type="checkbox" 
                  id="cookie-marketing"
                  className="h-5 w-5 text-blue-600 rounded cursor-pointer"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={acceptNecessaryOnly}
              >
                Refuser tout
              </Button>
              <Button
                size="sm"
                onClick={acceptSelected}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Enregistrer mes choix
              </Button>
            </div>
            
            <p className="text-xs text-slate-500 text-center">
              En savoir plus dans notre{' '}
              <a href="/privacy" className="text-blue-600 hover:underline">
                Politique de confidentialité
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
